import { renderSanitizedMermaidSvg } from "./mermaid-safe-render"

type SvgExportPayload = {
  svgForDataUri: string
  svgForFile: string
  width: number
  height: number
}

function parseSvgNumericAttribute(attributeValue: string | null): number | null {
  if (!attributeValue) return null
  const trimmed = attributeValue.trim()
  if (!trimmed || trimmed === "100%") return null
  const parsed = Number.parseFloat(trimmed)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return parsed
}

function getSvgIntrinsicSize(svgElement: SVGSVGElement): { width: number; height: number } | null {
  const width = parseSvgNumericAttribute(svgElement.getAttribute("width"))
  const height = parseSvgNumericAttribute(svgElement.getAttribute("height"))

  if (width && height) return { width, height }

  const viewBox = svgElement.getAttribute("viewBox")
  if (viewBox) {
    const parts = viewBox.split(/[\s,]+/).map((part) => Number.parseFloat(part))
    if (parts.length === 4 && parts.every((part) => Number.isFinite(part))) {
      const [, , viewBoxWidth, viewBoxHeight] = parts
      if (viewBoxWidth > 0 && viewBoxHeight > 0) {
        return { width: viewBoxWidth, height: viewBoxHeight }
      }
    }
  }

  try {
    const bbox = svgElement.getBBox()
    if (bbox.width > 0 && bbox.height > 0) return { width: bbox.width, height: bbox.height }
  } catch {
    // ignore
  }

  return null
}

function ensureSvgNamespaces(svgElement: SVGSVGElement) {
  if (!svgElement.getAttribute("xmlns")) {
    svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg")
  }
  if (!svgElement.getAttribute("xmlns:xlink")) {
    svgElement.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink")
  }
}

function getSvgExportPayload(svg: string): SvgExportPayload {
  const parsed = new DOMParser().parseFromString(svg, "image/svg+xml")
  const svgRoot = parsed.querySelector("svg")
  if (!(svgRoot instanceof SVGSVGElement)) {
    throw new Error("SVG root not found")
  }

  const svgClone = document.importNode(svgRoot, true)
  ensureSvgNamespaces(svgClone)

  const measurementContainer = document.createElement("div")
  measurementContainer.style.position = "fixed"
  measurementContainer.style.left = "-10000px"
  measurementContainer.style.top = "-10000px"
  measurementContainer.style.width = "0"
  measurementContainer.style.height = "0"
  measurementContainer.style.overflow = "hidden"
  measurementContainer.style.visibility = "hidden"
  measurementContainer.style.pointerEvents = "none"

  document.body.appendChild(measurementContainer)
  measurementContainer.appendChild(svgClone)

  const fallbackSize = { width: 800, height: 600 }
  const size = getSvgIntrinsicSize(svgClone) ?? fallbackSize

  if (!svgClone.getAttribute("viewBox")) {
    try {
      const bbox = svgClone.getBBox()
      if (bbox.width > 0 && bbox.height > 0) {
        svgClone.setAttribute("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`)
      } else {
        svgClone.setAttribute("viewBox", `0 0 ${size.width} ${size.height}`)
      }
    } catch {
      svgClone.setAttribute("viewBox", `0 0 ${size.width} ${size.height}`)
    }
  }

  svgClone.setAttribute("width", `${size.width}`)
  svgClone.setAttribute("height", `${size.height}`)

  const svgForDataUri = new XMLSerializer().serializeToString(svgClone)
  measurementContainer.remove()

  const svgForFile = `<?xml version="1.0" encoding="UTF-8"?>\n${svgForDataUri}`
  return { svgForDataUri, svgForFile, width: size.width, height: size.height }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function sanitizeFilename(title: string): string {
  return title.replace(/[^a-z0-9]/gi, "_") || "diagram"
}

export async function renderMermaidToSvg(code: string): Promise<string> {
  return renderSanitizedMermaidSvg(`export-${Date.now()}`, code)
}

export async function exportMermaidAsSvg(code: string, title: string): Promise<void> {
  const svg = await renderMermaidToSvg(code)
  const { svgForFile } = getSvgExportPayload(svg)
  const blob = new Blob([svgForFile], { type: "image/svg+xml;charset=utf-8" })
  downloadBlob(blob, `${sanitizeFilename(title)}.svg`)
}

export async function exportMermaidAsPng(code: string, title: string): Promise<void> {
  const svg = await renderMermaidToSvg(code)
  const { svgForDataUri, width, height } = getSvgExportPayload(svg)

  const maxCanvasDimension = 8192
  const targetScale = 2
  const scale = Math.max(1, Math.min(targetScale, maxCanvasDimension / Math.max(width, height)))

  const canvas = document.createElement("canvas")
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Could not get canvas context")

  ctx.setTransform(scale, 0, 0, scale, 0, 0)
  ctx.fillStyle = "white"
  ctx.fillRect(0, 0, width, height)

  const img = new Image()
  img.crossOrigin = "anonymous"
  const dataUri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgForDataUri)}`

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error("Failed to load SVG"))
    img.src = dataUri
  })

  ctx.drawImage(img, 0, 0, width, height)

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (nextBlob) => {
        if (!nextBlob) {
          reject(new Error("Failed to create PNG blob"))
          return
        }
        resolve(nextBlob)
      },
      "image/png",
      1.0
    )
  })

  downloadBlob(blob, `${sanitizeFilename(title)}.png`)
}
